// @ts-strict-ignore
import {
  Button, ButtonVariant, Modal, MultiSelect, useSnackBar,
} from '@usealloy/component-library';
import React from 'react';

import { getActionDataObjectKeyName, getActionName, getActionTypeText } from 'flow/service/actions';
import {
  ModalBody, ModalLink, ModalDescription,
} from 'flow/editor/components/MenuBar/MenuBarModals/AddActionModals/BaseAddActionModal';
import { resetNodeAndConnectorState, updateAction } from 'flow/store/redux/slice';
import {
  selectFocusedAction,
  selectPublishedAttributeActions,
  selectPublishedAttributes,
} from 'flow/store/redux/selectors';
import type { useModalGroup } from 'flow/editor/hooks';

import { routes } from '~/helpers/routes';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { buildPublishedAttributeOptions } from './helpers';
import type { FooterModalsNames } from '../types';
import type { PublishedAttributeActionTypes, ReplaceWithPublishedAttributeOption } from './types';


interface ReplaceWithPublishedAttributeModalProps {
  isOpen: boolean;
  modalGroup: ReturnType<typeof useModalGroup<FooterModalsNames>>;
}

export default function ReplaceWithPublishedAttributeModal({
  isOpen,
  modalGroup,
}: ReplaceWithPublishedAttributeModalProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const publishedAttributes = useAppSelector(selectPublishedAttributes);

  const focusedActionType = getActionDataObjectKeyName(
    focusedAction,
  ) as PublishedAttributeActionTypes;

  const publishedAttributesInWorkflow = useAppSelector(
    state => selectPublishedAttributeActions(state, focusedActionType),
  );

  const [
    selectedPublishedAttribute, setSelectedPublishedAttribute,
  ] = React.useState<ReplaceWithPublishedAttributeOption>();

  const addSnackbar = useSnackBar();

  const publishedAttributeOptions = buildPublishedAttributeOptions(
    focusedActionType,
    publishedAttributes,
    publishedAttributesInWorkflow,
  );

  const handleReplace = React.useCallback(() => {
    const publishedAttributeToReplaceWith = publishedAttributes
      .find(pa => pa.attributeToken === selectedPublishedAttribute.value);

    /**
       * when replacing an input with a PA, we simply "upgrade" the existing input action
       * to be a PA by replacing the input data object with the values from the PA.
       * this is enough; creating a new action for the PA, remapping all thresholds to it
       * and deleting the original input attribute should not be necessary unless buggy
       * behavior stemming from this is observed
      */
    dispatch(updateAction({
      path: `${focusedAction.publicId}.${focusedActionType}`,
      value: {
        ...focusedAction[focusedActionType],
        dataType: publishedAttributeToReplaceWith.dataType,
        jqFunction: publishedAttributeToReplaceWith.jqFilter,
        name: publishedAttributeToReplaceWith.attributeName,
        publishedAttributeToken: publishedAttributeToReplaceWith.attributeToken,
        useJq: String(publishedAttributeToReplaceWith.hasJqFilter),
        publishedDetails: publishedAttributeToReplaceWith,
      },
    }));

    dispatch(resetNodeAndConnectorState());

    const actionTypeText = getActionTypeText(focusedAction);
    const actionName = getActionName(focusedAction);
    addSnackbar({
      title: `Replaced ${actionTypeText} "${actionName}" with `
        + `Published Attribute "${publishedAttributeToReplaceWith.attributeName}"`,
      type: 'success',
      duration: 2_000,
    });
  }, [
    addSnackbar,
    dispatch,
    publishedAttributes,
    selectedPublishedAttribute,
    focusedAction,
    focusedActionType,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalGroup.close}
      header="Replace with Published Attribute"
      primaryAction={(
        <Button
          disabled={false}
          onClick={handleReplace}
          variant={ButtonVariant.primary}
        >
          Replace
        </Button>
      )}
      secondaryAction={(
        <Button
          onClick={modalGroup.close}
          variant={ButtonVariant.secondary}
        >
          Cancel
        </Button>
      )}
    >
      <ModalBody gap={4}>

        <ModalDescription>
          Published Attributes are used to store data that can be used in future evaluations.{' '}
          To view and edit all of your Published Attributes, visit
          <ModalLink
            target="_blank"
            rel="noreferrer"
            href={routes.v3.dashboard.settings.published_attributes}
          >Settings.
          </ModalLink>
        </ModalDescription>

        <MultiSelect
          isMulti={false}
          isOptionDisabled={(option: ReplaceWithPublishedAttributeOption) => option.disabled}
          isSearchable
          label={`Select a Published Attribute to replace "${getActionName(focusedAction)}"`}
          // @ts-expect-error single-values work out of the box without extra handling
          onChange={setSelectedPublishedAttribute}
          options={publishedAttributeOptions}
          placeholder="Select published attribute"
          selectedOptions={selectedPublishedAttribute}
          visibleSelectedOptions
          closeMenuOnSelect
        />

      </ModalBody>
    </Modal>
  );
}
